import { useState } from "react";
import { Box, Stack } from "@mui/material";
import { ChatSearchField } from "Components/Chat/ChatSearchField";
import { CHAT_PICKER_SECTION_SPACING } from "Components/Chat/chatConstants";
import { ChatUserSearchResults } from "Components/Chat/ChatUserSearchResults";
import type { SocialUserSearchDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useDebounce } from "Hooks/useDebounce";
import type { ReactNode } from "react";

const RESOURCES = Resources.Chat.NewChat;

type Props = {
  selectedUserIDs: ReadonlyArray<string>;
  /** Passed straight through; only the add-member dialog has any. */
  existingMemberIDs?: ReadonlyArray<string>;
  /** Passed straight through; only the group dialog suggests mutual followers. */
  showsMutualSuggestions?: boolean;
  /** Passed straight through; set when the conversation admits Full Moone personas only. */
  isFullMooneRequired?: boolean;
  /** Passed straight through; the direct dialog acts on click instead of selecting. */
  isMultiSelect?: boolean;
  /** Rendered between the input and the results — the selected-members row. */
  children?: ReactNode;
  onSelectUser: (user: SocialUserSearchDto) => void;
};

/**
 * Input half of the chat people picker; ChatUserSearchResults owns the state machine — the same
 * split the People page uses between UserSearchOverlay and UserSearchResults. The input stays
 * chat's own pill field: the mutuals one is welded to overlay mechanics (backdrop, click-away,
 * URL mirroring) that make no sense inside a dialog.
 *
 * The term deliberately SURVIVES a selection. Clearing it was right when picking someone removed
 * them from the list, but they now stay in it marked as selected — wiping the results would take
 * away the row the user has to click again to deselect.
 */
export const ChatUserSearch = ({
  selectedUserIDs,
  existingMemberIDs,
  showsMutualSuggestions,
  isFullMooneRequired,
  isMultiSelect,
  children,
  onSelectUser,
}: Props) => {
  const [search, setSearch] = useState("");
  const { t } = useResource();

  const debouncedSearch = useDebounce(search, 300);

  return (
    // Deliberately NOT a gapped Stack. `children` is the selected-members row, which collapses to
    // nothing — and a gapped flex child keeps its gap at zero height, then loses it the instant it
    // leaves the layout, which reads as a jump at the end of the close. So each part owns the space
    // above it instead, and the collapsing one takes its own spacing down with it.
    <Stack>
      <ChatSearchField
        value={search}
        onChange={setSearch}
        placeholder={t(RESOURCES.SearchPlaceholder)}
        isAutoFocused
      />
      {children}
      <Box pt={CHAT_PICKER_SECTION_SPACING}>
        <ChatUserSearchResults
          searchTerm={search}
          debouncedSearchTerm={debouncedSearch}
          selectedUserIDs={selectedUserIDs}
          existingMemberIDs={existingMemberIDs}
          showsMutualSuggestions={showsMutualSuggestions}
          isFullMooneRequired={isFullMooneRequired}
          isMultiSelect={isMultiSelect}
          onSelectUser={onSelectUser}
        />
      </Box>
    </Stack>
  );
};
